import React, { useEffect, useState } from 'react';
import { AddButton, AddCircleBlue, EditWithIcon, ManageActions, MinLoader, NoItemsYet, CustomizedSwitch, CustomDelete } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import {
    Colors,
    FindLoad,
    FindSuccessItem,
    Images,
    goalDataTypeEnums,
    FindErrorItem,
    convertSecondToMinute,
    useModal,
    isNotEmpty,
    PermissionList,
    RolePermission,
} from 'utils';
import { goalActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { OptionsChooseFromLibrary } from '../../../../../clinicalFragments';
import { renderOptionField, timeTypeEnums } from './constants';
import { Svg } from 'assets';
import moment from 'moment';

const DELETE_OPTION_ACTION = 'DELETE_OPTION';
const RESTORE_OPTION_ACTION = 'RESTORE_OPTION';
export const GoalOptions = () => {
    const goalById = useSelector((state) => state.goals.goalById);
    const [optionRow, setOptionRow] = useState(null);
    const [inputs, setInputs] = useState({});
    const [error, setError] = useState('');
    const [options, setOptions] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const OPTION_ACTION_TYPE = inputs?.id ? 'EDIT_OPTION' : 'CREATE_OPTION';
    const loadAdd = FindLoad(OPTION_ACTION_TYPE);
    const successCreate = FindSuccessItem(OPTION_ACTION_TYPE);
    const dispatch = useDispatch();
    const backError = FindErrorItem(OPTION_ACTION_TYPE);
    const { open } = useModal();
    const isPercentOrRating = goalById?.dataType === goalDataTypeEnums?.PERCENT || goalById?.dataType === goalDataTypeEnums?.RATING;
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const visibleOptions = showDeleted ? options : options.filter((option) => !option?.deletedAt);
    const alreadyDeleted = options?.find((option) => option?.deletedAt);

    useEffect(() => {
        if (goalById?.options?.length) {
            setOptions(goalById.options);
        }
    }, [goalById]);

    useEffect(() => {
        if (successCreate) {
            resetCreation();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(OPTION_ACTION_TYPE));
        }
    }, [successCreate]);

    const resetCreation = () => {
        setOptionRow(null);
        setError('');
        setInputs({ value: '0' });
        dispatch(httpRequestsOnErrorsActions.removeError(OPTION_ACTION_TYPE));
    };
    const handleSave = () => {
        if (isNotEmpty(inputs?.label) && (inputs?.value || inputs?.value === '0' || inputs?.value === 0)) {
            const params = {
                label: inputs?.label,
                value: +inputs?.value,
            };

            if (inputs?.timeType) {
                params.timeType = inputs?.timeType;
                params.value = convertSecondToMinute(inputs?.timeType, inputs?.value);
            }

            if (inputs?.id) {
                dispatch(goalActions.editOption(goalById?.id, inputs?.id, params));
            } else {
                dispatch(goalActions.createOption(goalById?.id, params));
            }
        } else {
            setError(!isNotEmpty(inputs?.label) ? 'label' : !inputs?.value ? 'value' : '');
        }
    };

    // const handleChange = (e) => {
    //     setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    //     if (error === e.target.name) {
    //         setError('');
    //     }
    // };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (value === '0' && goalById?.dataType !== goalDataTypeEnums?.PERCENT && goalById?.dataType !== goalDataTypeEnums?.RATING) {
            return;
        }

        setInputs((prev) => ({ ...prev, [name]: value }));

        if (error === name) {
            setError('');
        }
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination || source.index === destination.index) return;
        
        const visibleOptions = showDeleted ? options : options.filter(option => !option?.deletedAt);
        const movedItem = visibleOptions[source.index];
        const updatedOptions = Array.from(options);
        
        const originalSourceIndex = updatedOptions.findIndex(o => o.id === movedItem.id);
        updatedOptions.splice(originalSourceIndex, 1);
        
        const updatedVisibleOptions = Array.from(visibleOptions);
        updatedVisibleOptions.splice(source.index, 1);
        updatedVisibleOptions.splice(destination.index, 0, movedItem);
        
        const itemAfter = updatedVisibleOptions[destination.index + 1];
        
        let originalDestIndex;
        if (itemAfter) {
            originalDestIndex = updatedOptions.findIndex(o => o.id === itemAfter.id);
        } else {
            const itemBefore = updatedVisibleOptions[destination.index - 1];
            if (itemBefore) {
                const originalBeforeIndex = updatedOptions.findIndex(o => o.id === itemBefore.id);
                originalDestIndex = originalBeforeIndex + 1;
            } else {
                originalDestIndex = updatedOptions.length;
            }
        }
        
        updatedOptions.splice(originalDestIndex, 0, movedItem);
        setOptions(updatedOptions);

        const updatedGoal = {
            ...goalById,
            options: updatedOptions,
        };

        dispatch(
            goalActions.reorderOption(updatedGoal, {
                indexFrom: originalSourceIndex,
                indexTo: originalDestIndex,
            })
        );
    };

    const addOption = (closePopover) => {
        closePopover?.();
        setOptionRow('new');
        if (goalById?.dataType === goalDataTypeEnums?.PERCENT) {
            setInputs({ value: '1' });
        }
    };
    const handleEditRow = (option) => {
        setOptionRow(option?.id);
        const params = {
            id: option.id,
            label: option.label,
            value: option.value + '',
        };

        option?.timeType ? (params.timeType = option?.timeType) : delete params.timeType;

        if (option?.timeType === timeTypeEnums?.MINUTE) {
            params.value = option?.value / 60;
        } else {
            params.value = option?.value;
        }
        setInputs(params);
        setError('');
    };

    // CustomDelete shows the loader, clears the success flag and closes itself.
    const handleDelete = (option) => {
        open(
            <CustomDelete
                info={`Are you sure you want to delete "${option?.label}"?`}
                text={
                    <>
                        This option will be moved to Deleted Items and <b>won't be available for new sessions</b>. Existing session
                        data will remain unchanged. You can view deleted options using the <b>"Show deleted"</b> toggle.
                    </>
                }
                innerText={'Delete'}
                actionType={DELETE_OPTION_ACTION}
                handleDel={() => dispatch(goalActions.deleteOption(goalById?.id, option?.id))}
            />
        );
    };

    const handleRestore = (option) => {
        open(
            <CustomDelete
                info={`Restore "${option?.label}"?`}
                text={'The option comes back as it was when it was deleted.'}
                innerText={'Restore'}
                color={'blue'}
                actionType={RESTORE_OPTION_ACTION}
                handleDel={() => dispatch(goalActions.restoreOption(goalById?.id, option?.id))}
            />
        );
    };

    return (
        <div>
            <div className="flex-align-center" style={{ gap: '16px', marginBottom: '8px' }}>
                <p className="target-options-title" style={{ margin: 0, padding: 0 }}>
                    Options
                </p>
                {isPercentOrRating && (
                    <div className="flex-align-center" style={{ gap: '8px' }}>
                        <p className="show-deleted">Show deleted</p>
                        <CustomizedSwitch checked={showDeleted} handleClick={() => setShowDeleted(!showDeleted)} />
                    </div>
                )}
            </div>
            <div
                className="target-options-wrapper"
                style={{
                    background: options.length ? '#F2F4F8' : 'transparent',
                    padding: options.length ? '16px' : '0',
                }}>
                {options?.length > 0 ? (
                    <>
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="optionList">
                                {(provided) => (
                                    <div className="target-options-body" {...provided.droppableProps} ref={provided.innerRef}>
                                        {visibleOptions?.map((option, index) => {
                                            const isDeleted = !!option?.deletedAt;
                                            return (
                                                <Draggable
                                                    key={option.id}
                                                    draggableId={option.id}
                                                    index={index}
                                                    isDragDisabled={isDeleted || !hasPermission}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...(!isDeleted ? provided.dragHandleProps : {})}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                            }}>
                                                            {optionRow === option?.id ? (
                                                                <div className="target-options-row" style={{ gap: 10 }}>
                                                                    {goalById?.dataType === goalDataTypeEnums?.LATENCY ||
                                                                    goalById?.dataType === goalDataTypeEnums?.WHOLE_INTERVAL ||
                                                                    goalById?.dataType === goalDataTypeEnums?.PARTIAL_INTERVAL ||
                                                                    goalById?.dataType === goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING ? (
                                                                        <p className="option-label-box">{inputs?.label}</p>
                                                                    ) : (
                                                                        <input
                                                                            value={inputs?.label || ''}
                                                                            onChange={handleChange}
                                                                            placeholder="Write Option Name"
                                                                            className="target-option-input"
                                                                            type="text"
                                                                            name="label"
                                                                            style={{
                                                                                borderColor: backError?.error
                                                                                    ? '#FE7070'
                                                                                    : error === 'label'
                                                                                    ? '#FE7070'
                                                                                    : undefined,
                                                                            }}
                                                                        />
                                                                    )}
                                                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                                                        {renderOptionField(goalById, inputs, handleChange, error)}
                                                                        <div className="flex-align-center" style={{ gap: 16 }}>
                                                                            <button onClick={handleSave} className="save-option-btn">
                                                                                {loadAdd?.length ? (
                                                                                    <MinLoader
                                                                                        margin="0"
                                                                                        color={Colors.ThemeBlue}
                                                                                        position="relative"
                                                                                        width={13}
                                                                                        height={13}
                                                                                    />
                                                                                ) : (
                                                                                    <img src={Images.checkmarkBlue} alt="icon" />
                                                                                )}
                                                                                <p>Save</p>
                                                                            </button>
                                                                            <button onClick={resetCreation} className="cancel-option-btn">
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className="target-options-row"
                                                                    style={{
                                                                        background: snapshot.isDragging ? '#f0f0f0' : 'white',
                                                                        gap: 10,
                                                                    }}>
                                                                    <p className="option-label-box">{option?.label || '—'}</p>

                                                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                                                        {renderOptionField(goalById, option)}
                                                                        {isDeleted ? (
                                                                            <div className="flex-align-center" style={{ gap: 16 }}>
                                                                                <p
                                                                                    style={{
                                                                                        fontSize: 12,
                                                                                        color: '#9E9E9E',
                                                                                        whiteSpace: 'nowrap',
                                                                                        margin: 0,
                                                                                    }}>
                                                                                    {`Deleted by ${option?.deletedBy || '—'} | ${moment(
                                                                                        option?.deletedAt
                                                                                    ).format('MM/DD/YYYY')}`}
                                                                                </p>
                                                                                {hasPermission && (
                                                                                    <EditWithIcon
                                                                                        icon={Images.revertIcon}
                                                                                        type="button"
                                                                                        handleClick={() => handleRestore(option)}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <div className="flex-align-center" style={{ gap: 16 }}>
                                                                                {hasPermission && (
                                                                                    <EditWithIcon
                                                                                        icon={Images.edit}
                                                                                        type="button"
                                                                                        handleClick={() => handleEditRow(option)}
                                                                                    />
                                                                                )}
                                                                                {isPercentOrRating && hasPermission && (
                                                                                    <EditWithIcon
                                                                                        icon={Images.deleteTag}
                                                                                        type="button"
                                                                                        handleClick={() => handleDelete(option)}
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        {showDeleted && !alreadyDeleted && (
                            <NoItemsYet
                                height={'250px'}
                                text={'No deleted options'}
                                subTitle={'There are no deleted options to display.'}
                                image={Images.noClientYet}
                                containerStyles={{ position: 'relative' }}
                            />
                        )}
                    </>
                ) : (
                    <div style={{ background: '#F2F4F8', borderRadius: 8 }}>
                        <NoItemsYet
                            height={'250px'}
                            text={'You don’t have Option yet'}
                            subTitle={'Nothing new for now. Enjoy the silence while it lasts'}
                            image={Images.noClientYet}
                            containerStyles={{ position: 'relative' }}
                            renderButton={() =>
                                hasPermission ? (
                                    <ManageActions
                                        btnClass="reset-btn"
                                        renderBtnView={() => (
                                            <div style={{ width: 'fit-content', marginTop: 24 }}>
                                                <AddButton styles={{ width: 'fit-content' }} text={'Add Option'} />
                                            </div>
                                        )}
                                        renderBody={(closePopover) => (
                                            <div className="flex" style={{ flexDirection: 'column' }}>
                                                <button className="header-add-goals-items-btn" onClick={() => addOption(closePopover)}>
                                                    Add new option
                                                </button>

                                                <button
                                                    className="header-add-goals-items-btn"
                                                    onClick={() => {
                                                        open(
                                                            <OptionsChooseFromLibrary
                                                                CHOOSE_ACTION_TYPE={'CHOOSE_OPTIONS_FROM_LIBRARY'}
                                                                action={goalActions.chooseOptionsFromLibrary}
                                                                goalById={goalById}
                                                            />,
                                                            {
                                                                styles: {
                                                                    wrapperStyle: {
                                                                        width: 'calc(100vw - 32px)',
                                                                        maxWidth: '1370px',
                                                                    },
                                                                },
                                                            }
                                                        );
                                                        closePopover?.();
                                                    }}>
                                                    Choose from Library
                                                </button>
                                            </div>
                                        )}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        styleBodyItems={{ padding: 0 }}
                                    />
                                ) : null
                            }
                        />
                    </div>
                )}

                {optionRow === 'new' && (
                    <div>
                        <div className="target-options-row" style={{ gap: 10 }}>
                            {goalById?.dataType === goalDataTypeEnums?.LATENCY ||
                            goalById?.dataType === goalDataTypeEnums?.WHOLE_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums?.PARTIAL_INTERVAL ||
                            goalById?.dataType === goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING ? (
                                <p className="option-label-box">{inputs?.label}</p>
                            ) : (
                                <input
                                    value={inputs?.label || ''}
                                    onChange={handleChange}
                                    placeholder="Write Option Name"
                                    className="target-option-input"
                                    type="text"
                                    name="label"
                                    style={{
                                        borderColor: backError?.error ? '#FE7070' : error === 'label' ? '#FE7070' : undefined,
                                    }}
                                />
                            )}
                            <div className="flex-align-center" style={{ gap: 16 }}>
                                {renderOptionField(goalById, inputs, handleChange, error)}

                                <div className="flex-align-center" style={{ gap: 16 }}>
                                    <button onClick={handleSave} className="save-option-btn">
                                        {loadAdd?.length ? (
                                            <MinLoader margin="0" color={Colors.ThemeBlue} position="relative" width={13} height={13} />
                                        ) : (
                                            <img src={Images.checkmarkBlue} alt="icon" />
                                        )}
                                        <p>Add</p>
                                    </button>
                                    <button onClick={resetCreation} className="cancel-option-btn">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!optionRow &&
                    !!options?.length &&
                    hasPermission &&
                    goalById?.dataType !== goalDataTypeEnums?.LATENCY &&
                    goalById?.dataType !== goalDataTypeEnums?.WHOLE_INTERVAL &&
                    goalById?.dataType !== goalDataTypeEnums?.PARTIAL_INTERVAL &&
                    goalById?.dataType !== goalDataTypeEnums?.MOMENTARY_TIME_SAMPLING && (
                        <div className="flex-end" style={{ margin: '8px 0 0' }}>
                            <ManageActions
                                btnClass="reset-btn"
                                renderBtnView={(isOpen) => (
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <AddCircleBlue text={'Add Option'} />

                                        <Svg.ArrowRight2Svg
                                            style={{
                                                transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                                                filter: 'brightness(0) saturate(100%) invert(29%) sepia(94%) saturate(1578%) hue-rotate(201deg) brightness(90%) contrast(100%)',
                                            }}
                                        />
                                    </div>
                                )}
                                renderBody={(closePopover) => (
                                    <div className="flex" style={{ flexDirection: 'column' }}>
                                        <button className="header-add-goals-items-btn" onClick={() => addOption(closePopover)}>
                                            Add new option
                                        </button>

                                        <button
                                            className="header-add-goals-items-btn"
                                            onClick={() => {
                                                open(
                                                    <OptionsChooseFromLibrary
                                                        CHOOSE_ACTION_TYPE={'CHOOSE_OPTIONS_FROM_LIBRARY'}
                                                        action={goalActions.chooseOptionsFromLibrary}
                                                        goalById={goalById}
                                                    />,
                                                    {
                                                        styles: {
                                                            wrapperStyle: {
                                                                width: 'calc(100vw - 32px)',
                                                                maxWidth: '1370px',
                                                            },
                                                        },
                                                    }
                                                );
                                                closePopover?.();
                                            }}>
                                            Choose from Library
                                        </button>
                                    </div>
                                )}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                styleBodyItems={{ padding: 0 }}
                            />
                        </div>
                    )}
            </div>
        </div>
    );
};
